import React, { useState, useEffect } from 'react'
import { Bell, X, TrendingUp, TrendingDown, ShoppingCart, Wallet, Users, Award } from 'lucide-react'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { useTheme } from '../context/ThemeContext'
import axios from 'axios'
import { formatNumber } from '../utils/formatNumber'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function NotificationPanel({ token, address }) {
  const { isDarkMode } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (token && address) {
      fetchNotifications()
    }
  }, [token, address])

  const fetchNotifications = async () => {
    setIsLoading(true)
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      
      // Fetch ALL transactions involving the user (pending + completed)
      const transactionsResponse = await axios.get(`${API}/respect/transactions`, { headers })
      const allTransactions = transactionsResponse.data.filter(tx => 
        tx.from_wallet.toLowerCase() === address.toLowerCase() || 
        tx.to_wallet.toLowerCase() === address.toLowerCase()
      )

      // Fetch activity logs (withdrawals, purchases, etc.)
      let activityLogs = []
      try {
        const activityResponse = await axios.get(`${API}/activity-logs`, { headers })
        activityLogs = activityResponse.data || []
      } catch (err) {
        console.log('No activity logs available:', err)
      }

      // Fetch system notifications (bounty rewards, claim notifications, etc.)
      let systemNotifications = []
      try {
        const notifResponse = await axios.get(`${API}/notifications`, { headers })
        systemNotifications = (notifResponse.data.notifications || []).map(n => ({
          id: `system-${n.type}-${n.created_at}`,
          type: n.type,
          title: n.type === 'scout_reward' ? '🎉 Scout Reward Received!' :
                 n.type === 'bounty_deducted' ? '💰 Bounty Paid' :
                 n.type === 'claim_verified' ? '✅ Profile Verified!' :
                 n.type === 'claim_rejected' ? '❌ Claim Rejected' :
                 n.type === 'scout_profile_earned' ? '📈 Scouted Profile Earned Respect!' :
                 '📢 Notification',
          description: n.message,
          amount: 0,
          timestamp: n.created_at,
          icon: n.type === 'scout_reward' ? Award : 
                n.type === 'bounty_deducted' ? Wallet :
                n.type === 'claim_verified' ? TrendingUp :
                n.type === 'claim_rejected' ? TrendingDown :
                n.type === 'scout_profile_earned' ? TrendingUp : Bell,
          read: n.read
        }))
      } catch (err) {
        console.log('No system notifications available:', err)
      }

      // Create comprehensive notifications from all transactions
      const transactionNotifications = allTransactions.map(tx => {
        const isFrom = tx.from_wallet.toLowerCase() === address.toLowerCase()
        const isTo = tx.to_wallet.toLowerCase() === address.toLowerCase()
        const isPending = tx.status === 'pending'
        const isApproved = tx.status === 'approved'
        const isRejected = tx.status === 'rejected'
        
        let notification = {
          id: `tx-${tx.id}`,
          amount: tx.respect_amount,
          timestamp: tx.created_at,
        }

        // Pending transactions
        if (isPending) {
          notification.type = 'pending_vote'
          notification.title = isFrom ? 'Pending: Respect Given' : 'Pending: Respect Received'
          notification.description = `${tx.respect_amount > 0 ? '+' : ''}${formatNumber(tx.respect_amount)} respect awaiting votes (${tx.approvals?.length || 0}/10)`
          notification.icon = Users
        }
        // Approved transactions
        else if (isApproved) {
          if (isFrom) {
            notification.type = 'respect_given_approved'
            notification.title = '\u2705 Respect Given - Approved'
            notification.description = `Your ${tx.respect_amount > 0 ? '+' : ''}${formatNumber(tx.respect_amount)} respect gift was approved! Pledge returned + 20% bonus`
            notification.icon = TrendingUp
          } else {
            notification.type = 'respect_received_approved'
            notification.title = '\u2705 Respect Received - Approved'
            notification.description = `You received ${tx.respect_amount > 0 ? '+' : ''}${formatNumber(tx.respect_amount)} respect (approved by community)`
            notification.icon = TrendingUp
          }
        }
        // Rejected transactions
        else if (isRejected) {
          if (isFrom) {
            notification.type = 'respect_given_rejected'
            notification.title = '\u274c Respect Given - Rejected'
            notification.description = `Your ${tx.respect_amount > 0 ? '+' : ''}${formatNumber(tx.respect_amount)} respect gift was rejected. Lost 10% pledge`
            notification.icon = TrendingDown
          } else {
            notification.type = 'respect_received_rejected'
            notification.title = '\u274c Respect Transaction - Rejected'
            notification.description = `Transaction of ${tx.respect_amount > 0 ? '+' : ''}${formatNumber(tx.respect_amount)} respect was rejected. You received 2%`
            notification.icon = TrendingDown
          }
        }

        return notification
      })

      // Create notifications from activity logs (withdrawals, purchases, votes, etc.)
      const activityNotifications = activityLogs.map(log => {
        const activityType = log.activity_type || log.type
        
        if (activityType === 'withdrawal' || log.type === 'withdrawal') {
          return {
            id: `activity-${log.id}`,
            type: 'withdraw',
            title: '\ud83d\udcb8 Withdrawal Completed',
            description: `Withdrew ${formatNumber(log.amount)} PR to your wallet`,
            amount: -log.amount,
            timestamp: log.timestamp,
            icon: Wallet
          }
        } else if (activityType === 'purchase' || log.type === 'purchase') {
          return {
            id: `activity-${log.id}`,
            type: 'buy',
            title: '\ud83d\uded2 Purchase Completed',
            description: `Bought ${formatNumber(log.amount)} PR tokens`,
            amount: log.amount,
            timestamp: log.timestamp,
            icon: ShoppingCart
          }
        } else if (activityType === 'vote_cast') {
          return {
            id: `activity-${log.id}`,
            type: 'vote_cast',
            title: '\ud83d\uddf3\ufe0f Vote Recorded',
            description: log.description,
            amount: 0,
            timestamp: log.timestamp,
            icon: Users
          }
        } else if (activityType === 'vote_reward') {
          return {
            id: `activity-${log.id}`,
            type: 'vote_reward',
            title: '\ud83c\udfaf Voting Reward',
            description: log.description,
            amount: log.amount,
            timestamp: log.timestamp,
            icon: Award
          }
        } else if (activityType === 'respect_approved') {
          return {
            id: `activity-${log.id}`,
            type: 'respect_approved',
            title: '\u2705 Respect Approved',
            description: log.description,
            amount: log.amount,
            timestamp: log.timestamp,
            icon: TrendingUp
          }
        } else if (activityType === 'respect_given_approved') {
          return {
            id: `activity-${log.id}`,
            type: 'respect_given_approved',
            title: '\u2705 Your Respect Approved',
            description: log.description,
            amount: log.amount,
            timestamp: log.timestamp,
            icon: TrendingUp
          }
        } else if (activityType === 'respect_rejected') {
          return {
            id: `activity-${log.id}`,
            type: 'respect_rejected',
            title: '\u274c Respect Rejected',
            description: log.description,
            amount: log.amount,
            timestamp: log.timestamp,
            icon: TrendingDown
          }
        } else if (activityType === 'respect_given_rejected') {
          return {
            id: `activity-${log.id}`,
            type: 'respect_given_rejected',
            title: '\u274c Your Respect Rejected',
            description: log.description,
            amount: log.amount,
            timestamp: log.timestamp,
            icon: TrendingDown
          }
        } else if (activityType === 'leaderboard_achievement') {
          return {
            id: `activity-${log.id}`,
            type: 'leaderboard_achievement',
            title: '\ud83c\udfc6 Leaderboard Achievement!',
            description: log.description,
            amount: log.amount,
            timestamp: log.timestamp,
            icon: Award
          }
        }
        return null
      }).filter(n => n !== null)

      // Check if user has voted on transactions (voters get rewards)
      const votingRewards = allTransactions.filter(tx => {
        if (tx.status !== 'approved' && tx.status !== 'rejected') return false
        const hasVoted = tx.approvals?.includes(address.toLowerCase()) || tx.rejections?.includes(address.toLowerCase())
        return hasVoted
      }).map(tx => ({
        id: `vote-reward-${tx.id}`,
        type: 'voting_reward',
        title: '\ud83c\udfaf Voting Reward Earned',
        description: `Earned 1% reward for voting on transaction (${formatNumber(Math.abs(tx.respect_amount))} respect)`,
        amount: Math.abs(tx.respect_amount) * 0.01,
        timestamp: tx.approved_at || tx.created_at,
        icon: Award
      }))

      // Add pledge information for transactions user gave
      const pledgeNotifications = allTransactions
        .filter(tx => tx.from_wallet.toLowerCase() === address.toLowerCase() && tx.status === 'pending')
        .map(tx => ({
          id: `pledge-${tx.id}`,
          type: 'pledge',
          title: '\ud83d\udcb0 Pledge Made',
          description: `Pledged ${formatNumber(tx.pledge_amount || 0)} respect (12% of ${formatNumber(Math.abs(tx.respect_amount))})`,
          amount: -(tx.pledge_amount || 0),
          timestamp: tx.created_at,
          icon: Wallet
        }))

      // Combine all notifications
      const allNotifications = [...transactionNotifications, ...activityNotifications, ...votingRewards, ...pledgeNotifications, ...systemNotifications]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 50) // Show last 50 activities

      setNotifications(allNotifications)
      // Count unread - system notifications that are not read, plus pending votes and recent approvals/rejections
      const unreadSystemCount = systemNotifications.filter(n => !n.read).length
      setUnreadCount(unreadSystemCount + allNotifications.filter(n => n.type === 'pending_vote' || n.type.includes('approved') || n.type.includes('rejected')).length)
    } catch (err) {
      console.error('Failed to fetch notifications:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const getNotificationColor = (type) => {
    switch (type) {
      case 'respect_gain':
      case 'respect_received_approved':
      case 'respect_given_approved':
      case 'respect_approved':
      case 'claim_verified':
      case 'scout_reward':
      case 'scout_profile_earned':
        return 'text-green-600'
      case 'respect_loss':
      case 'respect_given_rejected':
      case 'respect_received_rejected':
      case 'respect_rejected':
      case 'claim_rejected':
        return 'text-red-600'
      case 'pending_vote':
        return 'text-blue-600'
      case 'voting_reward':
      case 'vote_reward':
        return 'text-yellow-600'
      case 'vote_cast':
        return 'text-blue-500'
      case 'pledge':
      case 'bounty_deducted':
        return 'text-orange-600'
      case 'buy':
        return 'text-purple-600'
      case 'withdraw':
        return 'text-indigo-600'
      default:
        return 'text-gray-600'
    }
  }

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="relative">
      {/* Bell Icon Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        size="sm"
        className="relative"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>

      {/* Notification Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <Card 
            className={`fixed sm:absolute right-0 sm:right-0 top-12 sm:top-12 left-0 sm:left-auto w-full sm:w-96 max-h-96 overflow-y-auto z-50 shadow-2xl ${
              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className={`text-lg ${isDarkMode ? 'text-gray-100' : ''}`}>
                Notifications
              </CardTitle>
              <Button
                onClick={() => setIsOpen(false)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="py-8 text-center">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : notifications.length === 0 ? (
                <div className={`py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {notifications.map((notification) => {
                    const Icon = notification.icon
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-1 ${getNotificationColor(notification.type)}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                              {notification.title}
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {notification.description}
                            </p>
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {formatTimestamp(notification.timestamp)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
